import React from 'react';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

/**
 * DrawerTabBar — barra de abas de drawer (`.tabs2` do mockup, promovida a
 * primitivo do DS em 2026-08-11 a partir do drawer de Detalhes do Lead):
 * abas alinhadas ao início, cada uma com a largura do próprio conteúdo
 * (NUNCA `flex-1` dividindo o espaço disponível), 14px semibold, ativa em
 * roxo com sublinhado de 2px. Ordem canônica em drawers de detalhe:
 * Detalhes → Atividades (→ extras). Ver UI_PATTERNS § Overlays item 7.
 *
 * Cores via CSS vars — dentro do `Drawer` shared resolvem pro tema claro
 * forçado; em hosts theme-aware (abas mobile de página) seguem o tema.
 */
export interface DrawerTabBarItem {
  id: string;
  label: React.ReactNode;
  /** Ícone opcional (14px, via catálogo Icon) */
  icon?: IconName;
  /** data-testid do botão da aba */
  testid?: string;
}

export interface DrawerTabBarProps {
  items: DrawerTabBarItem[];
  active: string;
  onChange: (id: string) => void;
  /** Bloqueia interação (ex.: drawer em loading) */
  disabled?: boolean;
  'data-testid'?: string;
}

export const DrawerTabBar: React.FC<DrawerTabBarProps> = ({
  items,
  active,
  onChange,
  disabled,
  'data-testid': dataTestId,
}) => (
  <div
    className="flex overflow-x-auto border-b"
    style={{ padding: '0 18px', borderColor: 'var(--theme-card-border)' }}
    data-testid={dataTestId}
  >
    {items.map((tab) => {
      const isActive = tab.id === active;
      return (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className="inline-flex items-center gap-[7px] text-sm font-semibold whitespace-nowrap transition-colors"
          style={{
            padding: '13px 4px',
            marginRight: 20,
            color: isActive ? 'var(--theme-primary)' : 'var(--theme-muted-text)',
            borderBottom: `2px solid ${isActive ? 'var(--theme-primary)' : 'transparent'}`,
            marginBottom: -1,
          }}
          disabled={disabled}
          data-testid={tab.testid}
        >
          {tab.icon && <Icon name={tab.icon} size={14} />}
          {tab.label}
        </button>
      );
    })}
  </div>
);

DrawerTabBar.displayName = 'DrawerTabBar';
